import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  getQuizCategories,
  createQuizCategory,
  updateQuizCategory,
  deleteQuizCategory,
  type QuizCategory,
} from "../../services/quiz-categories";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";

export default function QuizCategoriesPage() {
  const { t } = useTranslation();
  usePageTitle(t("admin.quizCategories.title", "Categorii Quiz"));

  const [categories, setCategories] = useState<QuizCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<QuizCategory | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    image_url: "",
    is_active: true,
  });

  // Image for the synthetic "Altele" (Other) bucket — stored in platform
  // settings, since that bucket isn't a real quiz_categories row.
  const [otherImage, setOtherImage] = useState<string>("");
  const [otherUploading, setOtherUploading] = useState(false);
  const otherFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCategories();
    api
      .get("/platform-settings")
      .then((r) => setOtherImage(r.data?.other_category_image || ""))
      .catch((e) => console.error("Failed to load platform settings:", e));
  }, []);

  const saveOtherImage = async (url: string) => {
    await api.patch("/platform-settings", { other_category_image: url });
    setOtherImage(url);
  };

  const handleOtherImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      try {
        setOtherUploading(true);
        const url = await uploadImage(file);
        await saveOtherImage(url);
      } catch (error) {
        console.error("Failed to upload image:", error);
        alert("Nu s-a putut încărca imaginea");
      } finally {
        setOtherUploading(false);
      }
    }
    if (otherFileRef.current) otherFileRef.current.value = "";
  };

  const handleOtherImageRemove = async () => {
    try {
      setOtherUploading(true);
      await saveOtherImage("");
    } catch (error) {
      console.error("Failed to remove image:", error);
    } finally {
      setOtherUploading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await getQuizCategories(true); // Show all including inactive
      setCategories(data);
    } catch (error) {
      console.error("Failed to load quiz categories:", error);
      alert("Nu s-au putut încărca categoriile");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({
      name: "",
      title: "",
      description: "",
      image_url: "",
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (category: QuizCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      title: category.title,
      description: category.description || "",
      image_url: category.image_url || "",
      is_active: category.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: "", title: "", description: "", image_url: "", is_active: true });
  };

  // Handle image upload
  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);

    // The image endpoint lives at /upload/image and takes the folder as a
    // query param; it returns processed variants ({ optimized, original,
    // thumbnail }), not a bare { url }.
    const response = await api.post<{ optimized?: string; original?: string; thumbnail?: string; url?: string }>(
      "/upload/image?folder=quiz-categories",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return response.data.optimized || response.data.original || response.data.url || "";
  };

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      try {
        setUploading(true);
        const url = await uploadImage(file);
        setFormData((prev) => ({ ...prev, image_url: url }));
      } catch (error) {
        console.error("Failed to upload image:", error);
        alert("Nu s-a putut încărca imaginea");
      } finally {
        setUploading(false);
      }
    }
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      try {
        setUploading(true);
        const url = await uploadImage(file);
        setFormData((prev) => ({ ...prev, image_url: url }));
      } catch (error) {
        console.error("Failed to upload image:", error);
        alert("Nu s-a putut încărca imaginea");
      } finally {
        setUploading(false);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.title) {
      alert("Te rugăm să completezi câmpurile obligatorii");
      return;
    }

    try {
      if (editingCategory) {
        await updateQuizCategory(editingCategory.id, {
          name: formData.name,
          title: formData.title,
          description: formData.description || undefined,
          image_url: formData.image_url || undefined,
          is_active: formData.is_active,
        });
      } else {
        await createQuizCategory({
          name: formData.name,
          title: formData.title,
          description: formData.description || undefined,
          image_url: formData.image_url || undefined,
          is_active: formData.is_active,
        });
      }

      await loadCategories();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save category:", error);
      alert(error.response?.data?.error || "Nu s-a putut salva categoria");
    }
  };

  const handleToggleActive = async (category: QuizCategory) => {
    try {
      await updateQuizCategory(category.id, {
        is_active: !category.is_active,
      });
      await loadCategories();
    } catch (error: any) {
      console.error("Failed to toggle active status:", error);
      alert(error.response?.data?.error || "Nu s-a putut actualiza categoria");
    }
  };

  const handleDelete = async (category: QuizCategory) => {
    if (!window.confirm(`Ștergi categoria "${category.title}"?`)) {
      return;
    }

    try {
      await deleteQuizCategory(category.id);
      await loadCategories();
    } catch (error: any) {
      console.error("Failed to delete category:", error);
      alert(error.response?.data?.error || "Nu s-a putut șterge categoria");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Conținut și Educație"
          title={t("quizCategories.title", "Categorii Quiz")}
          subtitle={t("quizCategories.description", "Gestionează categoriile pentru organizarea quiz-urilor")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openCreateModal}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("quizCategories.addCategory", "Adaugă Categorie")}
            </AdminButton>
          }
        />

        {/* "Altele" (Other) bucket image — configured here because that
            category is generated automatically and has no editable row. */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6 flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-slate-400 to-indigo-500 grid place-items-center">
            {otherImage ? (
              <img src={otherImage} alt="Altele" className="w-full h-full object-cover" />
            ) : (
              <svg className="w-7 h-7 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800">{t("quizCategories.otherTitle", "Categoria „Altele”")}</p>
            <p className="text-sm text-slate-500">
              {t("quizCategories.otherHint", "Categorie automată pentru quiz-urile fără categorie. Imaginea ei se setează aici.")}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input ref={otherFileRef} type="file" accept="image/*" onChange={handleOtherImageSelect} className="hidden" />
            <button
              type="button"
              disabled={otherUploading}
              onClick={() => otherFileRef.current?.click()}
              className="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 transition"
            >
              {otherUploading
                ? t("common.loading", "Se încarcă...")
                : otherImage
                ? t("quizCategories.changeImage", "Schimbă imaginea")
                : t("quizCategories.uploadImage", "Încarcă imagine")}
            </button>
            {otherImage && !otherUploading && (
              <button
                type="button"
                onClick={handleOtherImageRemove}
                className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition"
              >
                {t("common.remove", "Elimină")}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <p className="text-slate-500">{t("common.loading", "Se încarcă...")}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Statistics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("quizCategories.totalCategories", "Total Categorii")}</p>
                    <p className="text-2xl font-bold text-slate-800">{categories.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-50 rounded-xl">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("quizCategories.activeCategories", "Active")}</p>
                    <p className="text-2xl font-bold text-slate-800">{categories.filter((c) => c.is_active).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("quizCategories.withImages", "Cu Imagini")}</p>
                    <p className="text-2xl font-bold text-slate-800">{categories.filter((c) => c.image_url).length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Categories Grid */}
            {categories.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-slate-500">{t("quizCategories.noCategories", "Nicio categorie găsită")}</p>
                <button
                  onClick={openCreateModal}
                  className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                >
                  + {t("quizCategories.addFirst", "Adaugă prima categorie")}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3">{t("quizCategories.category", "Categorie")}</th>
                        <th className="px-4 py-3">{t("quizCategories.code", "Cod")}</th>
                        <th className="px-4 py-3 hidden md:table-cell">{t("quizCategories.description", "Descriere")}</th>
                        <th className="px-4 py-3 text-center">{t("quizCategories.quizCount", "Quiz-uri")}</th>
                        <th className="px-4 py-3 text-center">{t("quizCategories.order", "Ordine")}</th>
                        <th className="px-4 py-3 text-center">{t("common.status", "Status")}</th>
                        <th className="px-4 py-3 text-right">{t("common.actions", "Acțiuni")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {categories.map((category) => (
                        <tr key={category.id} className={`hover:bg-slate-50 transition-colors ${category.is_active ? "" : "opacity-60"}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                                {category.image_url ? (
                                  <img src={category.image_url} alt={category.title} className="w-full h-full object-cover" />
                                ) : (
                                  <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </div>
                              <span className="font-semibold text-slate-800">{category.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap">{category.name}</td>
                          <td className="px-4 py-3 hidden md:table-cell text-slate-600 max-w-xs truncate">{category.description || "—"}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-lg text-sm font-semibold tabular-nums ${
                              (category.quiz_count ?? 0) > 0 ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-400"
                            }`}>
                              {category.quiz_count ?? 0}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-500 tabular-nums">{category.order_no}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleToggleActive(category)}
                              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                category.is_active
                                  ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                                  : "bg-red-50 text-red-500 hover:bg-red-100"
                              }`}
                            >
                              {category.is_active ? t("common.active", "Activă") : t("common.inactive", "Inactivă")}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openEditModal(category)}
                                className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors"
                                title={t("common.edit", "Editează")}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(category)}
                                className="p-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                                title={t("common.delete", "Șterge")}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editingCategory
                    ? t("quizCategories.editCategory", "Editează Categorie")
                    : t("quizCategories.createCategory", "Creează Categorie")}
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Category Image Upload */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizCategories.image", "Imagine Categorie")}
                  </label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
                      isDragging
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-slate-300 hover:border-indigo-400 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploading ? (
                      <div className="py-4">
                        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm text-slate-500">{t("common.uploading", "Se încarcă...")}</p>
                      </div>
                    ) : formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData((prev) => ({ ...prev, image_url: "" }));
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="py-4">
                        <svg className="w-10 h-10 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-slate-600 font-medium">
                          {t("quizCategories.dragDropImage", "Trage și plasează imaginea aici")}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {t("quizCategories.orClickBrowse", "sau apasă pentru a naviga")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Name (slug) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizCategories.name", "Nume (slug)")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                      })
                    }
                    placeholder="ex: mental_health"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-mono"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {t("quizCategories.nameHint", "Doar litere mici și underscore. Folosit ca identificator.")}
                  </p>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizCategories.title", "Titlu Afișat")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ex: Sănătate Mintală"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    required
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizCategories.descriptionLabel", "Descriere")}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t("quizCategories.descriptionPlaceholder", "Scurtă descriere a acestei categorii...")}
                    rows={3}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none"
                  />
                </div>

                {/* Active Toggle */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {t("quizCategories.activeLabel", "Categoria este activă")}
                    </span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-sm disabled:opacity-50"
                  >
                    {editingCategory ? t("common.save", "Salvează") : t("common.create", "Creează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
